import { FeatureEnableState } from '../../../../Actions/FeatureAction';
import { GroupDataUpdateAction, GroupsDataAction } from '../../../../Actions/GroupsAction';
import { handleTempArchivedChats } from '../../../../Helpers/Chat/ChatHelper';
import { CHAT_TYPE_GROUP, GROUP_PROFILE_INFO_UPDATED, GROUP_USER_ADDED } from '../../../../Helpers/Chat/Constant';
import { setGroupParticipantsByGroupId } from '../../../../Helpers/Chat/Group';
import { formatUserIdToJid, getIdFromJid } from '../../../../Helpers/Chat/User';
import GroupsIdb from '../../../../Helpers/GroupsIdb';
import { Store } from '../../../../store';
import SDK from '../../../SDK';
import { encryptAndStoreInLocalStorage } from '../../WebChatEncryptDecrypt';
import logger from '../../../../Helpers/logger';
import { CONFIG } from './config';

// ============================================================================
// GROUP SYNCHRONIZATION MODULE (Blocking Architecture)
// ============================================================================
// Checks features → fetches ALL groups → fetches ALL participants → stores in IndexedDB → updates Redux → releases UI
// ============================================================================

// ============================================================================
// LOGGING
// ============================================================================

let groupSyncLogs = {};

const log = (id = 'Summary', message, ...args) => {
    if (!groupSyncLogs[id]) groupSyncLogs[id] = [];
    groupSyncLogs[id].push({ message, args, timestamp: new Date().toISOString() });
};

const logTime = (id, label, startTime) => {
    const duration = ((performance.now() - startTime) / 1000).toFixed(2);
    if (!groupSyncLogs[id]) groupSyncLogs[id] = [];
    groupSyncLogs[id].push({ type: 'time', label, duration: `${duration}s`, timestamp: new Date().toISOString() });
    return duration;
};

// ============================================================================
// CORE FUNCTIONS
// ============================================================================

/**
 * Fetches and stores feature flags
 * @returns {Promise<Object>} Feature data
 */
async function fetchFeatures() {
    log('Step 1', '🔧 Fetching feature flags');
    const startTime = performance.now();

    const featureRes = await SDK.getAvailableFeatures();

    if (featureRes?.statusCode === 200) {
        Store.dispatch(FeatureEnableState(featureRes.data));
        encryptAndStoreInLocalStorage('featureRestrictionFlags', featureRes.data);
        logTime('Step 1', 'Fetched features', startTime);
        return featureRes.data;
    }

    return {};
}

/**
 * Fetches ALL groups from server
 * @returns {Promise<Array>} All groups
 */
async function fetchAllGroups() {
    log('Step 3', '📥 Fetching all groups from server');
    const startTime = performance.now();

    const response = await SDK.getGroupsList();

    if (response?.statusCode !== 200 || !response.data) {
        log('Step 3', '⚠️ Failed to fetch groups');
        return [];
    }

    logTime('Step 3', `Fetched ${response.data.length} groups`, startTime);
    return response.data;
}

/**
 * Fetches participants for a single group
 * @param {Object} group - Group object
 * @returns {Promise<Array|null>} Participants or null
 */
async function fetchGroupParticipants(group) {
    const groupId = group.groupId;
    try {
        // If participants already in group object, use them
        if (group.participants) {
            return group.participants;
        }

        const groupJid = formatUserIdToJid(groupId, CHAT_TYPE_GROUP);
        const response = await SDK.getGroupParticipants(groupJid);

        if (response?.statusCode === 200) {
            return response.data.participants;
        }

        return null;
    } catch (error) {
        if (!groupSyncLogs[groupId]) groupSyncLogs[groupId] = [];
        groupSyncLogs[groupId].push({ type: 'error', message: 'Error fetching participants', error });
        return null;
    }
}

/**
 * Fetches participants for ALL groups in batches
 * @param {Array} groups - All groups
 * @returns {Promise<Array>} Groups with participants
 */
async function fetchAllParticipants(groups) {
    log(
        'Step 4',
        `👥 Fetching participants for ${groups.length} groups (batch size: ${CONFIG.PARTICIPANT_BATCH_SIZE})`,
    );
    const startTime = performance.now();

    const groupsWithParticipants = [];
    let processedCount = 0;

    // Process in batches
    for (let i = 0; i < groups.length; i += CONFIG.PARTICIPANT_BATCH_SIZE) {
        const batch = groups.slice(i, i + CONFIG.PARTICIPANT_BATCH_SIZE);

        const results = await Promise.allSettled(
            batch.map(async group => {
                const participants = await fetchGroupParticipants(group);
                return { ...group, participants };
            }),
        );

        results.forEach((result, _index) => {
            if (result.status === 'fulfilled') {
                const groupWithParticipants = result.value;
                groupsWithParticipants.push(groupWithParticipants);

                // Store in global helper for immediate access
                if (groupWithParticipants.participants) {
                    const groupJid = formatUserIdToJid(groupWithParticipants.groupId, CHAT_TYPE_GROUP);
                    setGroupParticipantsByGroupId(groupJid, groupWithParticipants.participants);
                }
            }
            processedCount++;
        });

        log('Step 4', `Processed ${processedCount}/${groups.length} groups`);
    }

    logTime('Step 4', `Fetched participants for ${groupsWithParticipants.length} groups`, startTime);
    return groupsWithParticipants;
}

/**
 * Stores all groups in IndexedDB
 * @param {Array} groups - All groups with participants
 */
async function storeGroupsInDB(groups) {
    log('Step 5', `💾 Storing ${groups.length} groups in IndexedDB`);
    const startTime = performance.now();

    await GroupsIdb.setGroups(groups);

    logTime('Step 5', `Stored ${groups.length} groups in IndexedDB`, startTime);
}

/**
 * Updates Redux store with all groups
 * @param {Array} groups - All groups
 */
function updateReduxStore(groups) {
    log('Summary', `🔄 Updating Redux store with ${groups.length} groups`);
    const startTime = performance.now();

    Store.dispatch(GroupsDataAction(groups));

    logTime('Summary', `Updated Redux store`, startTime);
}

// Server fields win over the cache; cached-only fields such as participants are kept.
async function refreshGroupsFromServer() {
    const freshGroups = await fetchAllGroups();
    if (freshGroups.length === 0) return;

    const knownIds = new Set((await GroupsIdb.getGroups()).map(group => group.groupId));
    const newGroups = await fetchAllParticipants(freshGroups.filter(group => !knownIds.has(group.groupId)));
    const newById = new Map(newGroups.map(group => [group.groupId, group]));

    // Read again: realtime updates may have landed while participants were loading.
    const cachedById = new Map((await GroupsIdb.getGroups()).map(group => [group.groupId, group]));
    const groups = freshGroups
        .map(group => ({ ...cachedById.get(group.groupId), ...newById.get(group.groupId), ...group }))
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    await storeGroupsInDB(groups);
    updateReduxStore(groups);
}

// ============================================================================
// MAIN EXPORT
// ============================================================================

/**
 * Main synchronization function - Cache-First Pattern
 * 1. Check features
 * 2. Check IndexedDB for cached groups
 * 3. If cache exists → use it (no API fetch)
 * 4. If no cache → fetch from API → fetch participants → store → update Redux
 */
export async function syncAllGroups() {
    groupSyncLogs = {};
    log('Step 0', '🚀 Starting group synchronization (Cache-First)');
    const overallStartTime = performance.now();

    try {
        // Step 1: Fetch and check features
        const features = await fetchFeatures();

        const isGroupChatEnabled = features.isGroupChatEnabled !== undefined ? features.isGroupChatEnabled : true;

        if (!isGroupChatEnabled) {
            log('Step 1', '⚠️ Group chat is disabled');
            return;
        }

        // Step 2: Check IndexedDB for cached groups
        log('Step 2', '📦 Checking IndexedDB for cached groups...');
        const cachedGroups = await GroupsIdb.getGroups();

        if (cachedGroups.length > 0) {
            // Cache hit - use cached data, no API fetch needed
            log('Step 2', `✨ Found ${cachedGroups.length} cached groups - using cached data`);
            updateReduxStore(cachedGroups);

            // Restore participants to global helper
            cachedGroups.forEach(group => {
                if (group.participants) {
                    const groupJid = formatUserIdToJid(group.groupId, CHAT_TYPE_GROUP);
                    setGroupParticipantsByGroupId(groupJid, group.participants);
                }
            });

            const cacheTime = logTime('Summary', '🎨 UI RELEASED (from cache)', overallStartTime);
            logger.debug(`[GroupSync] ✅ Cache hit! UI shown in ${cacheTime}s`, {
                details: groupSyncLogs,
            });
            // The cache misses changes made while this browser was away (a new image, name...).
            refreshGroupsFromServer().catch(error =>
                logger.error('[GroupSync] Background group refresh failed', error),
            );
            return; // Done - using cached data
        }

        // Step 3: No cache - fetch from API
        log('Step 3', '📭 No cached groups found - fetching from server');
        const freshGroups = await fetchAllGroups();

        if (freshGroups.length === 0) {
            log('Step 3', '⚠️ No groups available');
            return;
        }

        // Step 4: Fetch participants for all groups
        const groupsWithParticipants = await fetchAllParticipants(freshGroups);

        // Step 5: Store in IndexedDB
        await storeGroupsInDB(groupsWithParticipants);

        // Step 6: Update Redux
        updateReduxStore(groupsWithParticipants);

        // Step 7: Complete
        const totalDuration = logTime('Summary', '✅ SYNC COMPLETE', overallStartTime);
        logger.debug(`[GroupSync] ✅ SYNC COMPLETE: UI RELEASED in ${totalDuration}s`, {
            details: groupSyncLogs,
        });
    } catch (error) {
        if (!groupSyncLogs['Error']) groupSyncLogs['Error'] = [];
        groupSyncLogs['Error'].push({ type: 'error', message: 'Error during synchronization', error });
        logger.error('[GroupSync] ❌ Error during synchronization:', error, groupSyncLogs);
        throw error;
    }
}

// ============================================================================
// BATCH SYNCHRONIZATION HELPERS
// ============================================================================

/**
 * Synchronizes a batch of groups:
 * 1. Merges data in IndexedDB (preserving participants)
 * 2. Updates Redux store
 */
export async function syncGroupsBatch(groups) {
    if (!Array.isArray(groups) || groups.length === 0) return;

    // Update Redux
    Store.dispatch(GroupDataUpdateAction(groups));

    // Merge in IndexedDB to preserve participants/metadata
    for (const group of groups) {
        if (group.groupId) {
            await GroupsIdb.updateGroup(group.groupId, group);
        }
    }
}

/**
 * Fetches and synchronizes all groups
 */
async function fetchAndSyncGroupProfile(groupJid) {
    const response = await SDK.getGroupProfile(groupJid);
    if (response?.statusCode === 200 && response.data) {
        await syncGroupsBatch([response.data]);
    }
    return response;
}

// ============================================================================
// BACKWARD COMPATIBILITY EXPORTS
// ============================================================================

/**
 * Fetches and updates participants for a single group (backward compatibility)
 * @param {Object} group - Group object
 * @param {string} activeChatUserId - Active chat user ID
 */
export async function fetchAndUpdateGroupParticipants(group) {
    const participants = await fetchGroupParticipants(group);

    if (participants) {
        const groupJid = formatUserIdToJid(group.groupId, CHAT_TYPE_GROUP);
        setGroupParticipantsByGroupId(groupJid, participants);

        // Update in IndexedDB
        await GroupsIdb.updateGroup(group.groupId, { participants });
    }

    return participants;
}
/**
 * Handles group profile update notifications (backward compatibility)
 * @param {Object} res - Notification object
 */
export async function handleGroupProfileUpdate(res) {
    if (!res?.groupJid) return;
    const groupId = getIdFromJid(res.groupJid);

    if ([GROUP_USER_ADDED, GROUP_PROFILE_INFO_UPDATED].includes(res.msgType)) {
        await fetchAndSyncGroupProfile(res.groupJid);
    }

    // 1. Fetch and update participants
    await fetchAndUpdateGroupParticipants({ groupId });

    // 2. Handle temporary archived chats
    handleTempArchivedChats(res.groupJid, CHAT_TYPE_GROUP);

    // 5. Update in IndexedDB (including metadata if available in res)
    await GroupsIdb.updateGroup(groupId, res);

    // 6. Update Redux store with specific group update
    Store.dispatch(GroupDataUpdateAction(res));
}
